import * as Sentry from "@sentry/react";

import { getUserAttributes } from "@skilitics/designer-auth/attributes";
import { getCognitoUsername } from "@skilitics/designer-auth/state";

import { SENTRY_WIDGET_ID } from "../../ui/GlobalStyles.ts";
import type { ILoggingClient } from "../types.ts";

export interface SentryConfig {
  readonly dsn: string;
  readonly env?: string; // default "production"
}

export const init = ({ dsn, env }: SentryConfig): ILoggingClient => {
  const feedbackIntegration = Sentry.feedbackIntegration({
    id: SENTRY_WIDGET_ID,
    colorScheme: "system",
    showEmail: false,
    showName: false,
    showBranding: false,
    autoInject: false,
  });

  const client = Sentry.init({
    dsn,
    environment: env,

    beforeSend(event) {
      const username = getCognitoUsername();
      const attributes = getUserAttributes();

      // Check if it is an exception, and if so, show the report dialog
      // https://docs.sentry.io/platforms/javascript/guides/react/user-feedback/
      if (event.exception && event.event_id) {
        Sentry.showReportDialog({
          eventId: event.event_id,
          user: !attributes
            ? undefined
            : {
                email: attributes.email,
                name: `${attributes.given_name} ${attributes.family_name}`,
              },
        });
      }
      event.user =
        !username || !attributes
          ? undefined
          : {
              id: username,
              email: attributes.email,
              username: `${attributes.given_name} ${attributes.family_name}`,
            };

      // Used to have state with sessionId, but it was never being passed?
      // event.extra = {
      //   state: getState?.(),
      // };
      return event;
    },

    integrations: [
      Sentry.globalHandlersIntegration({
        // we handle these in our own global handler and forward to
        // the error handler if it makes sense
        onerror: false,
        onunhandledrejection: false,
      }),
      feedbackIntegration,
    ],
  });

  if (!client) {
    console.warn("Sentry client not initialized");
  }

  return {
    leaveBreadcrumb: (_message, _metadata) => {
      // client.captureCheckIn()??
    },
    warning: (message) => {
      client?.captureMessage(message, "warning", {});
    },
    error: (error) => {
      client?.captureException(error);
    },
  };
};
